import React, { useState, useEffect, useCallback } from 'react';
import './WisdomArchive.css';

const DIMENSIONS = [
  { key: 'identity', label: 'Identity', color: '#E8A87C', icon: '◉' },
  { key: 'values_beliefs', label: 'Values & Beliefs', color: '#85CDCA', icon: '◈' },
  { key: 'personality', label: 'Personality', color: '#D4A5FF', icon: '◇' },
  { key: 'emotional_landscape', label: 'Emotional', color: '#FF6B6B', icon: '♡' },
  { key: 'relationships', label: 'Relationships', color: '#4ECDC4', icon: '∞' },
  { key: 'life_story', label: 'Life Story', color: '#FFE66D', icon: '☆' },
  { key: 'wisdom_perspective', label: 'Wisdom', color: '#A8E6CF', icon: '✧' },
  { key: 'voice_expression', label: 'Expression', color: '#FF8A5C', icon: '♪' },
];

export default function WisdomArchive({ token, api, onClose }) {
  const [entries, setEntries] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTag, setFilterTag] = useState(null);
  const [filterDim, setFilterDim] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formTags, setFormTags] = useState('');
  const [formDimension, setFormDimension] = useState('');
  const [saving, setSaving] = useState(false);

  // Generate state
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState(null);

  // Expanded card
  const [expandedId, setExpandedId] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchEntries = useCallback(async () => {
    try {
      let url = `${api}/wisdom/entries?`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      if (filterTag) url += `tag=${encodeURIComponent(filterTag)}&`;
      if (filterDim) url += `dimension=${encodeURIComponent(filterDim)}&`;

      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setEntries(data.entries || []);
        setAllTags(data.tags || []);
      }
    } catch (e) {
      console.error('Failed to fetch wisdom entries:', e);
    } finally {
      setLoading(false);
    }
  }, [api, token, search, filterTag, filterDim]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const openAdd = () => {
    setEditEntry(null);
    setFormTitle('');
    setFormContent('');
    setFormTags('');
    setFormDimension('');
    setDraft(null);
    setShowModal(true);
  };

  const openEdit = (entry) => {
    setEditEntry(entry);
    setFormTitle(entry.title);
    setFormContent(entry.content);
    setFormTags((entry.tags || []).join(', '));
    setFormDimension(entry.dimension || '');
    setDraft(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditEntry(null);
    setDraft(null);
  };

  const handleSave = async () => {
    if (!formTitle.trim() || !formContent.trim()) return;
    setSaving(true);
    const tags = formTags.split(',').map(t => t.trim()).filter(Boolean);
    const body = {
      title: formTitle.trim(),
      content: formContent.trim(),
      tags,
      dimension: formDimension || null,
    };

    try {
      if (editEntry) {
        await fetch(`${api}/wisdom/entries/${editEntry.id}`, {
          method: 'PUT', headers: authHeaders, body: JSON.stringify(body),
        });
      } else {
        if (draft?.source_memory_ids) body.source_memory_ids = draft.source_memory_ids;
        await fetch(`${api}/wisdom/entries`, {
          method: 'POST', headers: authHeaders, body: JSON.stringify(body),
        });
      }
      closeModal();
      await fetchEntries();
    } catch (e) {
      console.error('Save failed:', e);
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${api}/wisdom/entries/${id}`, {
        method: 'DELETE', headers: authHeaders,
      });
      await fetchEntries();
    } catch (e) {
      console.error('Delete failed:', e);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const body = {};
      if (filterDim) body.dimension = filterDim;
      const res = await fetch(`${api}/wisdom/generate`, {
        method: 'POST', headers: authHeaders, body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        const d = data.draft;
        setDraft(d);
        setFormTitle(d.title || '');
        setFormContent(d.content || '');
        setFormTags((d.tags || []).join(', '));
        setFormDimension(d.dimension || filterDim || '');
        setShowModal(true);
      }
    } catch (e) {
      console.error('Generate failed:', e);
    }
    setGenerating(false);
  };

  const getDimMeta = (key) => DIMENSIONS.find(d => d.key === key);

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="wisdom-container">
      <button className="wisdom-close" onClick={onClose}>✕</button>

      <div className="wisdom-header">
        <h2 className="wisdom-title">Wisdom Archive</h2>
        <p className="wisdom-subtitle">Life lessons, advice, and insights distilled from your memories</p>
      </div>

      {/* Toolbar: search + filters + actions */}
      <div className="wisdom-toolbar">
        <div className="wisdom-search-wrap">
          <input
            className="wisdom-search"
            placeholder="Search wisdom..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
        </div>

        <div className="wisdom-filters">
          {/* Dimension filter */}
          <select
            className="wisdom-dim-filter"
            value={filterDim || ''}
            onChange={e => setFilterDim(e.target.value || null)}
          >
            <option value="">All dimensions</option>
            {DIMENSIONS.map(d => (
              <option key={d.key} value={d.key}>{d.icon} {d.label}</option>
            ))}
          </select>

          {/* Tag chips */}
          {allTags.length > 0 && (
            <div className="wisdom-tag-chips">
              {filterTag && (
                <button className="wisdom-tag-chip active" onClick={() => setFilterTag(null)}>
                  {filterTag} ✕
                </button>
              )}
              {allTags.filter(t => t !== filterTag).slice(0, 8).map(t => (
                <button key={t} className="wisdom-tag-chip" onClick={() => setFilterTag(t)}>
                  {t}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="wisdom-actions-bar">
          <button className="wisdom-add-btn" onClick={openAdd}>+ Add Wisdom</button>
          <button className="wisdom-generate-btn" onClick={handleGenerate} disabled={generating}>
            {generating ? 'Generating...' : '✧ Generate from Memories'}
          </button>
        </div>
      </div>

      {/* Entries grid */}
      <div className="wisdom-grid">
        {loading && <div className="wisdom-empty">Loading wisdom entries...</div>}
        {!loading && entries.length === 0 && (
          <div className="wisdom-empty">
            <div className="wisdom-empty-icon">✧</div>
            <p>No wisdom entries yet</p>
            <p className="wisdom-empty-sub">Add your own or generate from your memories</p>
          </div>
        )}
        {entries.map(entry => {
          const dim = getDimMeta(entry.dimension);
          const isExpanded = expandedId === entry.id;
          return (
            <div
              key={entry.id}
              className={`wisdom-card ${isExpanded ? 'wisdom-card-expanded' : ''}`}
              style={{ '--wis-color': dim?.color || '#A8E6CF' }}
              onClick={() => setExpandedId(isExpanded ? null : entry.id)}
            >
              <div className="wisdom-card-top">
                {dim && <span className="wisdom-card-dim" style={{ color: dim.color }}>{dim.icon} {dim.label}</span>}
                <span className="wisdom-card-date">{formatDate(entry.created_at)}</span>
              </div>
              <h3 className="wisdom-card-title">{entry.title}</h3>
              <p className={`wisdom-card-content ${isExpanded ? '' : 'wisdom-card-content-clamp'}`}>
                {entry.content}
              </p>
              {entry.tags && entry.tags.length > 0 && (
                <div className="wisdom-card-tags">
                  {entry.tags.map(t => (
                    <span key={t} className="wisdom-card-tag" onClick={e => { e.stopPropagation(); setFilterTag(t); }}>{t}</span>
                  ))}
                </div>
              )}
              {isExpanded && (
                <div className="wisdom-card-actions" onClick={e => e.stopPropagation()}>
                  <button className="wisdom-card-edit" onClick={() => openEdit(entry)}>Edit</button>
                  <button className="wisdom-card-delete" onClick={() => handleDelete(entry.id)}>Delete</button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="wisdom-modal-overlay" onClick={closeModal}>
          <div className="wisdom-modal" onClick={e => e.stopPropagation()}>
            <h3 className="wisdom-modal-title">
              {draft ? 'Review Generated Wisdom' : editEntry ? 'Edit Wisdom Entry' : 'Add Wisdom Entry'}
            </h3>
            {draft && (
              <div className="wisdom-draft-badge">Generated from {draft.source_memory_ids?.length || 0} memories — review and save</div>
            )}
            <label className="wisdom-modal-label">Title</label>
            <input
              className="wisdom-modal-input"
              value={formTitle}
              onChange={e => setFormTitle(e.target.value)}
              placeholder="A concise life lesson..."
            />
            <label className="wisdom-modal-label">Content</label>
            <textarea
              className="wisdom-modal-textarea"
              value={formContent}
              onChange={e => setFormContent(e.target.value)}
              placeholder="Expand on the wisdom..."
              rows={5}
            />
            <label className="wisdom-modal-label">Tags (comma-separated)</label>
            <input
              className="wisdom-modal-input"
              value={formTags}
              onChange={e => setFormTags(e.target.value)}
              placeholder="family, resilience, growth"
            />
            <label className="wisdom-modal-label">Dimension</label>
            <select
              className="wisdom-modal-input"
              value={formDimension}
              onChange={e => setFormDimension(e.target.value)}
            >
              <option value="">None</option>
              {DIMENSIONS.map(d => (
                <option key={d.key} value={d.key}>{d.icon} {d.label}</option>
              ))}
            </select>
            <div className="wisdom-modal-btns">
              <button className="wisdom-modal-cancel" onClick={closeModal}>Cancel</button>
              <button className="wisdom-modal-save" onClick={handleSave} disabled={saving || !formTitle.trim() || !formContent.trim()}>
                {saving ? 'Saving...' : editEntry ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
